import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackbarNotificationService } from '@raxapp/notifications/snackbar/snackbar-notification.service';
import {
    ShowErrorNotificationDetailsDialogComponent,
    ShowErrorNotificationDetailsDialogData,
} from './show-error-notification-details-dialog/show-error-notification-details-dialog.component';

interface ErrorWithQueueItemId {
    errorData: unknown;
    errorReason: string;
    queueItemId: string;
}

@Injectable({
    providedIn: 'root',
})
export class ShowErrorNotificationService {
    private notificationService = inject(SnackbarNotificationService);
    private matDialog = inject(MatDialog);
    private destroyRef = inject(DestroyRef);

    private errorsWithQueueItemId: ErrorWithQueueItemId[] = [];
    private maxErrorsWithQueueItemId = 10;

    constructor() {
        this.subscribeObservers();
    }

    private subscribeObservers(): void {
        this.notificationService.notificationActionTriggered.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            const errorWithQueueItemId = this.errorsWithQueueItemId.find((errorWithQueueItemId) => {
                return errorWithQueueItemId.queueItemId === data.queueItemId;
            });

            if (errorWithQueueItemId) {
                this.openShowErrorNotificationDetailsDialog({
                    errorReason: errorWithQueueItemId.errorReason,
                });
            }
        });
    }

    private openShowErrorNotificationDetailsDialog(
        dialogData: ShowErrorNotificationDetailsDialogData,
    ): MatDialogRef<ShowErrorNotificationDetailsDialogComponent, void> {
        const dialogRef: MatDialogRef<ShowErrorNotificationDetailsDialogComponent, void> = this.matDialog.open(
            ShowErrorNotificationDetailsDialogComponent,
            {
                data: dialogData,
                disableClose: false,
            },
        );
        return dialogRef;
    }

    public show(errorData: unknown, message: string, errorReason: string): void {
        if (errorData == undefined) return;

        const queueItemId = this.notificationService.addErrorMessageWithButtonToQueue(message);
        this.errorsWithQueueItemId.push({ errorData: errorData, errorReason: errorReason, queueItemId: queueItemId });
        if (this.errorsWithQueueItemId.length > this.maxErrorsWithQueueItemId) {
            this.errorsWithQueueItemId.splice(0, this.errorsWithQueueItemId.length - this.maxErrorsWithQueueItemId);
        }
    }
}
